import { useRef, useState, useEffect } from 'react';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import { Input } from '../Input';
import { extractAddress, type ParsedAddress } from './addressUtils';

export type { ParsedAddress } from './addressUtils';

const libraries = ['places'] as const;
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_CLIENT_KEY || '';

type AddressAutocompleteProps = {
  onAddressSelect: (address: ParsedAddress) => void;
  label?: string;
  hideLabel?: boolean;
  placeholder?: string;
  hasError?: boolean;
  value?: string;
  onChange?: (value: string) => void;
};

const AddressAutocomplete = ({
  onAddressSelect,
  label = 'Address',
  hideLabel = false,
  placeholder = 'Search for an address',
  hasError = false,
  value = '',
  onChange,
}: AddressAutocompleteProps) => {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const placesDivRef = useRef<HTMLDivElement | null>(null);
  const [inputValue, setInputValue] = useState(value);
  const typedInputRef = useRef(value);

  useEffect(() => {
    setInputValue(value);
    typedInputRef.current = value;
  }, [value]);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: libraries as any,
    ...(GOOGLE_MAPS_API_KEY ? {} : { preventLoad: true }),
  });

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (!place?.place_id) return;

    const service = new google.maps.places.PlacesService(placesDivRef.current || document.createElement('div'));
    service.getDetails(
      { placeId: place.place_id, fields: ['formatted_address', 'address_components', 'geometry'] },
      (details, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && details?.address_components) {
          // Pass typed input as fallback for when Google returns city-level data
          const parsed = extractAddress(
            details.address_components,
            details.formatted_address || '',
            typedInputRef.current,
          );
          if (details.geometry?.location) {
            parsed.latitude = details.geometry.location.lat();
            parsed.longitude = details.geometry.location.lng();
          }
          setInputValue(parsed.formatted_address);
          typedInputRef.current = parsed.formatted_address;
          onAddressSelect(parsed);
        }
      },
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    typedInputRef.current = newValue;
    onChange?.(newValue);
  };

  if (!isLoaded) {
    return (
      <div className="w-full">
        <Input label={label} hideLabel={hideLabel} placeholder="Loading..." disabled hasError={hasError} />
      </div>
    );
  }

  return (
    <>
      <Autocomplete
        onLoad={(ref) => (autocompleteRef.current = ref)}
        onPlaceChanged={handlePlaceChanged}
        options={{ types: ['address'], componentRestrictions: { country: 'us' } }}
      >
        <div className="w-full">
          <Input
            label={label}
            hideLabel={hideLabel}
            placeholder={placeholder}
            hasError={hasError}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter') e.preventDefault();
            }}
          />
        </div>
      </Autocomplete>
      <div ref={placesDivRef} className="hidden" />
    </>
  );
};

export default AddressAutocomplete;
